import { ErrorApi } from './errores'
import type { components } from './tipos'

/**
 * Enlace con el backend real, trazabis-agendamiento (NestJS, rutas bajo /api/v1).
 *
 * Las pantallas y los hooks siguen pidiendo las rutas «lógicas» con las que se
 * escribió la interfaz (`/api/clientes`, `/api/agendamiento/tabla`...), que eran
 * las del antiguo backend FastAPI del dashboard. Esta tabla es el ÚNICO sitio que
 * sabe cómo se llama cada cosa en el backend real: traduce la ruta, renombra los
 * parámetros y los campos del cuerpo, y devuelve la respuesta con la forma que
 * esperan los componentes (`modelos.ts`). La tabla completa, con las diferencias
 * de campos y los huecos, está documentada en `docs/enlace-backend.md`.
 *
 * Lo que el backend no ofrece (un hueco) lanza un `ErrorApi` 501 `no_disponible`
 * sin llegar a la red, y `EstadoError` lo pinta como «Función no disponible» en
 * vez de romper la pantalla.
 */

/** Prefijo de la API versionada. Las rutas son relativas: proxy de Vite o nginx. */
export const BASE_API = '/api/v1'

/** Código de error con el que se marcan las funciones que el backend no tiene. */
export const NO_DISPONIBLE = 'no_disponible'

export type Metodo = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
export const METODOS: readonly Metodo[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']
type Objeto = Record<string, unknown>
export type Parametros = Record<string, string | number | undefined | null>

/** Petición ya en términos del backend real (ruta relativa a BASE_API). */
export interface PeticionReal {
  metodo?: Metodo
  parametros?: Parametros
  cuerpo?: unknown
  senal?: AbortSignal
  /** `archivo`: la respuesta es un fichero (Blob) y no se traduce. */
  formato?: 'json' | 'archivo'
}

export type PedirReal = (ruta: string, peticion?: PeticionReal) => Promise<unknown>

interface Contexto {
  /** Segmentos variables de la ruta lógica (`:id`, `:canal`...). */
  ruta: Record<string, string>
  parametros: Parametros
  cuerpo: unknown
  senal?: AbortSignal
}

export interface Enlace {
  metodo: Metodo
  /** Ruta lógica que usan los hooks, con `:segmentos`. */
  logica: string
  /** Ruta real bajo BASE_API, con los mismos `:segmentos`. */
  real?: string
  /** Renombrado de parámetros de consulta: lógico -> real. */
  parametros?: Record<string, string>
  /** Valor de los segmentos: lógico -> real (p. ej. el canal `clientes`). */
  segmentos?: Record<string, Record<string, string>>
  cuerpo?: (cuerpo: unknown) => unknown
  respuesta?: (datos: unknown) => unknown
  /** Para los casos que no son una sola llamada (o dependen de los parámetros). */
  componer?: (pedir: PedirReal, contexto: Contexto) => Promise<unknown>
}

// ---------------------------------------------------------------------------
// Ayudantes de renombrado (superficiales a propósito: los `meta` de las
// conversaciones traen claves arbitrarias que no hay que tocar).
// ---------------------------------------------------------------------------

function esObjeto(valor: unknown): valor is Objeto {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor)
}

/** Copia `objeto` cambiando el nombre de las claves de `mapa` (origen -> destino). */
export function renombrar(objeto: unknown, mapa: Record<string, string>): unknown {
  if (!esObjeto(objeto)) return objeto
  const salida: Objeto = {}
  for (const [clave, valor] of Object.entries(objeto)) {
    salida[mapa[clave] ?? clave] = valor
  }
  return salida
}

/** Aplica `fn` a cada elemento de un array (o del array `items` de una página). */
function cadaUno(fn: (item: unknown) => unknown) {
  return (datos: unknown): unknown => {
    if (Array.isArray(datos)) return datos.map(fn)
    if (esObjeto(datos) && Array.isArray(datos.items)) return { ...datos, items: datos.items.map(fn) }
    return datos
  }
}

/** Aplica `fn` al valor de la clave `clave` si existe. */
function enCampo(clave: string, fn: (valor: unknown) => unknown) {
  return (datos: unknown): unknown => {
    if (!esObjeto(datos) || !(clave in datos)) return datos
    return { ...datos, [clave]: fn(datos[clave]) }
  }
}

function encadenar(...pasos: ((d: unknown) => unknown)[]) {
  return (datos: unknown) => pasos.reduce((acumulado, paso) => paso(acumulado), datos)
}

// --- Formas concretas -------------------------------------------------------

/** Cliente agendable: el backend dice `address`; la interfaz, `direccion`. */
const clienteAgendable = (c: unknown) => renombrar(c, { address: 'direccion' })

/** Cliente de la tabla de agendamiento: `routes` -> `rutas`. */
const clienteTabla = (c: unknown) => renombrar(c, { routes: 'rutas', address: 'direccion' })

/** Pedido habitual: el bono y el litraje se llaman `voucher` y `liters`. */
const pedidoHabitual = (p: unknown) => renombrar(p, { voucher: 'bono', liters: 'litraje' })

/** `client` -> `cliente`, adaptando el cliente con `fn`. */
function conCliente(fn: (c: unknown) => unknown) {
  return encadenar((d) => renombrar(d, { client: 'cliente' }), enCampo('cliente', fn))
}

/**
 * Recálculo: el backend da `pctAfectado` como fracción (0..1, y como texto en el
 * listado porque la columna es numeric); la pantalla lo pinta como «N%».
 */
function porcentajeRecalculo(datos: unknown): unknown {
  if (!esObjeto(datos) || !('pctAfectado' in datos)) return datos
  const fraccion = Number(datos.pctAfectado)
  return { ...datos, pctAfectado: Number.isFinite(fraccion) ? Math.round(fraccion * 1000) / 10 : 0 }
}

/** Pendiente/gestión del ciclo: `cycleStatus` -> `estadoCiclo`. */
const clienteCiclo = (c: unknown) => renombrar(c, { cycleStatus: 'estadoCiclo' })

/** Perfil 360: `address`/`tags`/`cycleStatus` -> `direccion`/`etiquetas`/`estadoCiclo`. */
const perfil360 = (p: unknown) =>
  renombrar(p, { address: 'direccion', tags: 'etiquetas', cycleStatus: 'estadoCiclo' })

/**
 * El backend valida `extras[].product_id` como entero (herencia de Odoo), pero
 * su propio catálogo usa UUID. Un id no entero se manda como `null`: el
 * resolvedor sigue casando el extra por nombre en vez de devolver un 400.
 */
function extraParaBackend(extra: unknown): unknown {
  if (!esObjeto(extra)) return extra
  const id = extra.product_id
  return { ...extra, product_id: Number.isInteger(id) ? id : null }
}

type ResolverDto = components['schemas']['ResolverDto']
type PublicarDto = components['schemas']['PublicarDto']

function cuerpoResolver(cuerpo: unknown): ResolverDto | unknown {
  if (!esObjeto(cuerpo)) return cuerpo
  const clientes = Array.isArray(cuerpo.clientes) ? cuerpo.clientes : []
  return {
    clients: clientes.map((entrada) => {
      const e = renombrar(entrada, { bono: 'voucher', litraje: 'liters' })
      if (!esObjeto(e)) return e
      return Array.isArray(e.extras) ? { ...e, extras: e.extras.map(extraParaBackend) } : e
    }),
  } as ResolverDto
}

function cuerpoPublicar(cuerpo: unknown): PublicarDto | unknown {
  if (!esObjeto(cuerpo)) return cuerpo
  const clientes = Array.isArray(cuerpo.clientes) ? cuerpo.clientes : []
  return {
    fecha_reparto: cuerpo.fecha_reparto,
    clients: clientes.map((c) => renombrar(c, { direccion: 'address' })),
  } as PublicarDto
}

/** Repartos del mes: cada día trae `client`, `route` y `voucher`. */
function repartosMes(datos: unknown): unknown {
  if (!esObjeto(datos)) return datos
  const salida: Objeto = {}
  for (const [fecha, repartos] of Object.entries(datos)) {
    salida[fecha] = Array.isArray(repartos)
      ? repartos.map((r) => renombrar(r, { client: 'cliente', route: 'ruta', voucher: 'bono' }))
      : repartos
  }
  return salida
}

// --- Calendario semanal (se compone con el mensual) ---------------------------

const DIAS = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'] as const

function isoLocal(fecha: Date): string {
  const m = String(fecha.getMonth() + 1).padStart(2, '0')
  const d = String(fecha.getDate()).padStart(2, '0')
  return `${fecha.getFullYear()}-${m}-${d}`
}

/** Días (lunes a domingo) de la semana natural que contiene `hoy`. */
export function diasDeLaSemana(hoy: Date): Date[] {
  const lunes = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - ((hoy.getDay() + 6) % 7))
  return Array.from({ length: 7 }, (_, i) => new Date(lunes.getFullYear(), lunes.getMonth(), lunes.getDate() + i))
}

/**
 * El backend no tiene vista semanal: solo `scheduling/calendario-mes`. La semana
 * en curso se arma con el mes (o los dos meses) que la contienen. Con `semana`
 * explícita (s1..s4 del ciclo) no hay equivalente: hueco.
 */
async function calendarioSemana(pedir: PedirReal, contexto: Contexto, hoy = new Date()): Promise<unknown> {
  if (contexto.parametros.semana) {
    throw new ErrorApi(
      501,
      NO_DISPONIBLE,
      'El calendario por semana del ciclo (s1..s4) aún no está disponible en el backend.',
    )
  }
  const dias = diasDeLaSemana(hoy)
  const meses = [...new Set(dias.map((d) => isoLocal(d).slice(0, 7)))]
  const porFecha: Objeto = {}
  for (const mes of meses) {
    const datos = repartosMes(await pedir('/scheduling/calendario-mes', { parametros: { mes }, senal: contexto.senal }))
    if (esObjeto(datos)) Object.assign(porFecha, datos)
  }
  const semana: Record<string, unknown[]> = {}
  for (const dia of dias) {
    const repartos = porFecha[isoLocal(dia)]
    semana[DIAS[dia.getDay()]] = Array.isArray(repartos)
      ? repartos.filter(esObjeto).map((r) => ({
          id: r.id,
          cliente: r.cliente,
          ruta: r.ruta ?? null,
          resumen: r.bono ?? null,
        }))
      : []
  }
  return semana
}

/**
 * La vista GE del agendamiento pedía `/api/clientes?tag=ge`. El backend no filtra
 * clientes por etiqueta, pero tiene la sección `ge`: se traduce a la forma
 * `ClienteGE` de `FiltroGE.tsx`. `tipo_productos` no lo da el backend (hueco
 * parcial) y de los repartos solo da el último.
 */
async function clientes(pedir: PedirReal, contexto: Contexto): Promise<unknown> {
  const { tag, ruta, ...resto } = contexto.parametros
  if (tag === undefined || tag === null || tag === '') {
    return pedir('/clients', { parametros: { ...resto, route: ruta }, senal: contexto.senal })
  }
  if (String(tag).toLowerCase() !== 'ge') {
    throw new ErrorApi(501, NO_DISPONIBLE, 'El backend no filtra clientes por etiqueta.')
  }
  const datos = await pedir('/ge', { senal: contexto.senal })
  const filas = esObjeto(datos) && Array.isArray(datos.items) ? datos.items.filter(esObjeto) : []
  return {
    items: filas.map((g) => ({
      contact_id: g.contactId,
      nombre: g.nombre,
      code: g.cif ?? null,
      ciudad: g.poblacion ?? null,
      tipo_productos: [],
      ultimos_repartos: g.ultimoReparto ? [g.ultimoReparto] : [],
    })),
    total: filas.length,
    page: 1,
    page_size: filas.length,
  }
}

/**
 * Parte de ruta del día. El calculado ya trae `etiqueta`/`direccion` (y además
 * `tag`/`address`); el externo, que escribía n8n, solo `tag`/`address`. Se
 * rellena el nombre de la interfaz cuando falta, sin tocar lo demás.
 */
function parteRuta(datos: unknown): unknown {
  if (!esObjeto(datos) || !Array.isArray(datos.grupos)) return datos
  return {
    ...datos,
    grupos: datos.grupos.map((g) => {
      if (!esObjeto(g)) return g
      const paradas = Array.isArray(g.paradas)
        ? g.paradas.map((p) => (esObjeto(p) && p.direccion === undefined ? { ...p, direccion: p.address ?? null } : p))
        : []
      return { ...g, etiqueta: g.etiqueta ?? g.tag ?? '', paradas }
    }),
  }
}

// ---------------------------------------------------------------------------
// La tabla
// ---------------------------------------------------------------------------

export const ENLACES: readonly Enlace[] = [
  // Sesión y meta
  { metodo: 'GET', logica: '/api/me', real: '/me' },
  {
    metodo: 'GET',
    logica: '/api/filtros',
    real: '/filtros',
    respuesta: encadenar(
      (d) => renombrar(d, { routes: 'rutas' }),
      enCampo('semanas', cadaUno((o) => renombrar(o, { tag: 'etiqueta' }))),
      enCampo('rutas', cadaUno((o) => renombrar(o, { tag: 'etiqueta' }))),
    ),
  },

  // Clientes / Logística
  { metodo: 'GET', logica: '/api/clientes', componer: clientes },
  {
    metodo: 'GET',
    logica: '/api/clientes/:id',
    real: '/clients/:id',
    respuesta: (d) => renombrar(d, { tags: 'etiquetas' }),
  },
  { metodo: 'POST', logica: '/api/clientes/:id/geocodificar', real: '/clients/:id/geocodificar' },
  { metodo: 'GET', logica: '/api/clientes/:id/ultima-factura', real: '/clients/:id/ultima-factura' },
  { metodo: 'POST', logica: '/api/clientes/:id/baja', real: '/clients/:id/baja' },
  { metodo: 'POST', logica: '/api/clientes/:id/reactivar', real: '/clients/:id/reactivar' },
  // Perfil 360 del cliente. El historial sale del puerto interno de albaranes y
  // las notas de la tabla propia del módulo: no hace falta ningún servicio.
  { metodo: 'GET', logica: '/api/clientes/:id/perfil', real: '/clients/:id/perfil', respuesta: perfil360 },
  {
    metodo: 'GET',
    logica: '/api/clientes/perfil/por-telefono/:tel',
    real: '/clients/perfil/por-telefono/:tel',
    respuesta: perfil360,
  },
  { metodo: 'GET', logica: '/api/clientes/:id/albaranes', real: '/clients/:id/albaranes' },
  { metodo: 'GET', logica: '/api/clientes/:id/notas', real: '/clients/:id/notas' },
  { metodo: 'POST', logica: '/api/clientes/:id/notas', real: '/clients/:id/notas' },
  { metodo: 'DELETE', logica: '/api/clientes/:id/notas/:nota', real: '/clients/:id/notas/:nota' },
  { metodo: 'GET', logica: '/api/productos/mas-consumidos', real: '/products/mas-consumidos' },
  {
    metodo: 'GET',
    logica: '/api/rutas/carga',
    real: '/routes/carga',
    respuesta: cadaUno((c) => renombrar(c, { route: 'ruta' })),
  },

  // Avisos
  { metodo: 'GET', logica: '/api/avisos', real: '/alerts' },
  { metodo: 'GET', logica: '/api/avisos/resumen', real: '/alerts/summary' },

  // Agendamiento
  {
    metodo: 'GET',
    logica: '/api/agendamiento/clientes',
    real: '/scheduling/clients',
    respuesta: cadaUno(conCliente(clienteAgendable)),
  },
  {
    metodo: 'GET',
    logica: '/api/agendamiento/tabla',
    real: '/scheduling/tabla',
    parametros: { ruta: 'route' },
    respuesta: cadaUno(encadenar(conCliente(clienteTabla), enCampo('pedido_habitual', pedidoHabitual))),
  },
  { metodo: 'GET', logica: '/api/agendamiento/productos', real: '/scheduling/products' },
  { metodo: 'GET', logica: '/api/agendamiento/historial/:id', real: '/scheduling/history/:id' },
  {
    metodo: 'POST',
    logica: '/api/agendamiento/resolver',
    real: '/scheduling/resolver',
    cuerpo: cuerpoResolver,
    respuesta: enCampo('resultados', (r) => (Array.isArray(r) ? r.map(conCliente(clienteAgendable)) : r)),
  },
  {
    metodo: 'POST',
    logica: '/api/agendamiento/publicar',
    real: '/scheduling/publicar',
    cuerpo: cuerpoPublicar,
    respuesta: (d) => renombrar(d, { alert: 'aviso' }),
  },
  {
    metodo: 'GET',
    logica: '/api/agendamiento/calendario-mes',
    real: '/scheduling/calendario-mes',
    respuesta: repartosMes,
  },
  { metodo: 'GET', logica: '/api/agendamiento/calendario', componer: (p, c) => calendarioSemana(p, c) },

  // Ciclo semanal (Confirmar agendamiento)
  {
    metodo: 'GET',
    logica: '/api/ciclo/pendientes',
    real: '/cycle/pendientes',
    respuesta: cadaUno(clienteCiclo),
  },
  {
    metodo: 'GET',
    logica: '/api/ciclo/gestion',
    real: '/cycle/gestion',
    respuesta: encadenar(
      (d) => renombrar(d, { clients: 'clientes' }),
      enCampo('clientes', (l) => (Array.isArray(l) ? l.map(clienteCiclo) : l)),
    ),
  },
  {
    metodo: 'POST',
    logica: '/api/ciclo/pendientes/:id/marcar',
    real: '/cycle/pendientes/:id/marcar',
    respuesta: clienteCiclo,
  },

  // Incidencias (derivaciones)
  {
    metodo: 'GET',
    logica: '/api/derivaciones',
    real: '/referrals',
    respuesta: cadaUno((d) => renombrar(d, { emailType: 'tipoCorreo' })),
  },
  {
    metodo: 'POST',
    logica: '/api/derivaciones/:id/marcar',
    real: '/referrals/:id/marcar',
    respuesta: (d) => renombrar(d, { emailType: 'tipoCorreo' }),
  },

  // Recálculo de etiquetas
  {
    metodo: 'GET',
    logica: '/api/etiquetas/ejecuciones',
    real: '/tags/executions',
    respuesta: cadaUno(porcentajeRecalculo),
  },
  {
    metodo: 'GET',
    logica: '/api/etiquetas/ejecuciones/:id/cambios',
    real: '/tags/executions/:id/cambios',
    respuesta: cadaUno((c) => renombrar(c, { tag: 'campo' })),
  },
  {
    metodo: 'POST',
    logica: '/api/etiquetas/recalcular/simulacion',
    real: '/tags/recalculate/simulacion',
    respuesta: porcentajeRecalculo,
  },

  // Auditoría, grandes empresas y workflows
  { metodo: 'GET', logica: '/api/auditoria', real: '/audit' },
  { metodo: 'GET', logica: '/api/ge', real: '/ge' },
  { metodo: 'POST', logica: '/api/ge/:id/recurrencia', real: '/ge/:id/recurrencia' },
  { metodo: 'GET', logica: '/api/workflows', real: '/workflows' },
  { metodo: 'GET', logica: '/api/workflows/:id/ejecuciones', real: '/workflows/:id/executions' },
  { metodo: 'POST', logica: '/api/workflows/:id/estado', real: '/workflows/:id/status' },

  // Conversaciones: el canal `clientes` se llama `clients` en el backend.
  {
    metodo: 'GET',
    logica: '/api/conversaciones/:canal/sesiones',
    real: '/conversations/:canal/sesiones',
    segmentos: { canal: { clientes: 'clients' } },
    respuesta: cadaUno((s) => renombrar(s, { tag: 'etiqueta', messages: 'mensajes' })),
  },
  {
    metodo: 'GET',
    logica: '/api/conversaciones/:canal/sesiones/:sid/mensajes',
    real: '/conversations/:canal/sesiones/:sid/mensajes',
    segmentos: { canal: { clientes: 'clients' } },
  },

  // --- Spec 002: operar sin servicios externos ---------------------------------
  // Identidad con roles (el menú de admin) e integraciones visibles (RF-37, RF-48).
  { metodo: 'GET', logica: '/api/sesion/yo', real: '/session/yo' },
  { metodo: 'GET', logica: '/api/integraciones', real: '/integrations' },

  // Clientes: alta, edición (etiquetas y coordenadas) y precios pactados (RF-40, RF-42).
  { metodo: 'POST', logica: '/api/clientes', real: '/clients', respuesta: (d) => renombrar(d, { tags: 'etiquetas' }) },
  { metodo: 'PATCH', logica: '/api/clientes/:id', real: '/clients/:id', respuesta: (d) => renombrar(d, { tags: 'etiquetas' }) },
  { metodo: 'GET', logica: '/api/clientes/:id/precios', real: '/clients/:id/precios' },
  { metodo: 'PUT', logica: '/api/clientes/:id/precios/:producto', real: '/clients/:id/precios/:producto' },
  { metodo: 'DELETE', logica: '/api/clientes/:id/precios/:producto', real: '/clients/:id/precios/:producto' },

  // Catálogo interno (RF-41).
  { metodo: 'GET', logica: '/api/productos', real: '/products' },
  { metodo: 'POST', logica: '/api/productos', real: '/products' },
  { metodo: 'PATCH', logica: '/api/productos/:id', real: '/products/:id' },
  { metodo: 'POST', logica: '/api/productos/:id/desactivar', real: '/products/:id/desactivar' },
  { metodo: 'POST', logica: '/api/productos/:id/reactivar', real: '/products/:id/reactivar' },

  // Albaranes internos (RF-43).
  { metodo: 'GET', logica: '/api/albaranes', real: '/delivery-notes', parametros: { cliente: 'contactId' } },
  { metodo: 'GET', logica: '/api/albaranes/:id', real: '/delivery-notes/:id' },
  { metodo: 'GET', logica: '/api/albaranes/:id/pdf', real: '/delivery-notes/:id/pdf' },
  { metodo: 'POST', logica: '/api/albaranes/:id/anular', real: '/delivery-notes/:id/anular' },

  // Ruta del día (RF-44). El parte del día ya existía; calcular y el PDF por ruta son nuevos.
  { metodo: 'GET', logica: '/api/ruta-dia/:fecha', real: '/delivery-reports/:fecha', respuesta: parteRuta },
  { metodo: 'POST', logica: '/api/ruta-dia/:fecha/calcular', real: '/delivery-reports/:fecha/calcular' },
  { metodo: 'GET', logica: '/api/ruta-dia/:fecha/rutas/:etiqueta/pdf', real: '/delivery-reports/:fecha/rutas/:etiqueta.pdf' },

  // Aviso semanal en modo manual (RF-45).
  { metodo: 'GET', logica: '/api/avisos-manuales', real: '/cycle/avisos-manuales' },
  { metodo: 'POST', logica: '/api/avisos-manuales/:id/enviado', real: '/cycle/avisos-manuales/:id/enviado' },
  { metodo: 'POST', logica: '/api/ciclo/respuestas/:id', real: '/cycle/respuestas/:id' },

  // Usuarios (RF-46) y ajustes (RF-47), solo admin.
  { metodo: 'GET', logica: '/api/usuarios', real: '/users' },
  { metodo: 'POST', logica: '/api/usuarios', real: '/users' },
  { metodo: 'PATCH', logica: '/api/usuarios/:id', real: '/users/:id' },
  { metodo: 'POST', logica: '/api/usuarios/:id/password', real: '/users/:id/password' },
  { metodo: 'GET', logica: '/api/ajustes', real: '/settings' },
  { metodo: 'POST', logica: '/api/ajustes', real: '/settings' },
]

// ---------------------------------------------------------------------------
// Resolución
// ---------------------------------------------------------------------------

function casar(patron: string, ruta: string): Record<string, string> | null {
  const a = patron.split('/')
  const b = ruta.split('/')
  if (a.length !== b.length) return null
  const valores: Record<string, string> = {}
  for (let i = 0; i < a.length; i++) {
    if (a[i].startsWith(':')) {
      if (!b[i]) return null
      valores[a[i].slice(1)] = b[i]
    } else if (a[i] !== b[i]) {
      return null
    }
  }
  return valores
}

export function buscarEnlace(ruta: string, metodo: Metodo): { enlace: Enlace; valores: Record<string, string> } | null {
  for (const enlace of ENLACES) {
    if (enlace.metodo !== metodo) continue
    const valores = casar(enlace.logica, ruta)
    if (valores) return { enlace, valores }
  }
  return null
}

function rellenar(plantilla: string, valores: Record<string, string>): string {
  // Los valores van codificados: un id o una etiqueta nunca deben poder
  // cambiar la ruta (barras, `?`, `#`).
  return plantilla.replace(/:([a-z]+)/gi, (_, nombre: string) => encodeURIComponent(valores[nombre] ?? ''))
}

/**
 * Ejecuta la petición lógica `ruta` contra el backend real. `pedir` recibe rutas
 * relativas a BASE_API. Una ruta que ya empieza por BASE_API pasa tal cual.
 */
export async function ejecutarEnlace(
  pedir: PedirReal,
  ruta: string,
  opciones: PeticionReal = {},
): Promise<unknown> {
  const metodo = opciones.metodo ?? 'GET'
  if (ruta.startsWith(`${BASE_API}/`)) return pedir(ruta.slice(BASE_API.length), opciones)

  const encontrado = buscarEnlace(ruta, metodo)
  if (!encontrado) {
    throw new ErrorApi(501, NO_DISPONIBLE, `La ruta ${metodo} ${ruta} no está enlazada con el backend.`)
  }
  const { enlace, valores } = encontrado

  const reales: Record<string, string> = {}
  for (const [nombre, valor] of Object.entries(valores)) {
    reales[nombre] = enlace.segmentos?.[nombre]?.[valor] ?? valor
  }
  const parametros: Parametros = {}
  for (const [clave, valor] of Object.entries(opciones.parametros ?? {})) {
    parametros[enlace.parametros?.[clave] ?? clave] = valor
  }

  if (enlace.componer) {
    return enlace.componer(pedir, {
      ruta: reales,
      parametros,
      cuerpo: opciones.cuerpo,
      senal: opciones.senal,
    })
  }

  const datos = await pedir(rellenar(enlace.real ?? '', reales), {
    metodo,
    parametros: opciones.parametros ? parametros : undefined,
    cuerpo: enlace.cuerpo && opciones.cuerpo !== undefined ? enlace.cuerpo(opciones.cuerpo) : opciones.cuerpo,
    senal: opciones.senal,
    formato: opciones.formato,
  })
  if (opciones.formato === 'archivo') return datos
  return enlace.respuesta ? enlace.respuesta(datos) : datos
}

/** Solo para pruebas: la semana compuesta con una fecha fija. */
export const _calendarioSemana = calendarioSemana
